import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { Plus, Pin, PinOff, Save, X, Edit2, Trash2, FileText, Eye, EyeOff, Loader2 } from 'lucide-react';
import { NEWS_CATEGORIES } from '../pages/News';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const blankPost = () => ({
    id: null,
    title: '',
    body: '',
    category: 'new_feature',
    is_published: false,
    is_pinned: false,
});

export default function AdminNewsTab() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null); // null | post obj
    const [saving, setSaving] = useState(false);

    const fetchAll = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/admin/news`);
            setPosts(data || []);
        } catch (e) {
            toast.error('Failed to load posts');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const startNew = () => setEditing(blankPost());
    const startEdit = (p) => setEditing({ ...p });

    const save = async () => {
        if (!editing.title.trim() || !editing.body.trim()) {
            toast.error('Title and body are required');
            return;
        }
        setSaving(true);
        try {
            if (editing.id) {
                // Update
                const { data } = await axios.patch(`${API_URL}/api/admin/news/${editing.id}`, {
                    title: editing.title,
                    body: editing.body,
                    category: editing.category,
                    is_published: editing.is_published,
                    is_pinned: editing.is_pinned,
                });
                setPosts(prev => prev.map(p => p.id === data.id ? data : p));
                toast.success('Post updated');
            } else {
                // Create
                const { data } = await axios.post(`${API_URL}/api/admin/news`, {
                    title: editing.title,
                    body: editing.body,
                    category: editing.category,
                    is_published: editing.is_published,
                    is_pinned: editing.is_pinned,
                });
                setPosts(prev => [data, ...prev]);
                toast.success(editing.is_published ? 'Post published' : 'Draft saved');
            }
            setEditing(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    const togglePublish = async (p) => {
        try {
            const { data } = await axios.patch(`${API_URL}/api/admin/news/${p.id}`, {
                is_published: !p.is_published,
            });
            setPosts(prev => prev.map(x => x.id === data.id ? data : x));
            toast.success(data.is_published ? 'Published' : 'Unpublished');
        } catch (e) {
            toast.error('Failed');
        }
    };

    const togglePin = async (p) => {
        try {
            const { data } = await axios.patch(`${API_URL}/api/admin/news/${p.id}`, {
                is_pinned: !p.is_pinned,
            });
            setPosts(prev => prev.map(x => x.id === data.id ? data : x));
        } catch (e) {
            toast.error('Failed');
        }
    };

    const remove = async (p) => {
        if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API_URL}/api/admin/news/${p.id}`);
            setPosts(prev => prev.filter(x => x.id !== p.id));
            toast.success('Deleted');
        } catch (e) {
            toast.error('Failed');
        }
    };

    return (
        <div className="space-y-4" data-testid="admin-news-tab">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-semibold">News & Updates</h2>
                    <p className="text-sm text-muted-foreground">Author posts that appear in the in-app news feed and (for public categories) on the landing page.</p>
                </div>
                {!editing && (
                    <Button onClick={startNew} className="bg-indigo-600 hover:bg-indigo-700" data-testid="admin-news-new-btn">
                        <Plus className="w-4 h-4 mr-1" /> New post
                    </Button>
                )}
            </div>

            {/* Editor */}
            {editing && (
                <Card className="border-indigo-300 dark:border-indigo-700" data-testid="admin-news-editor">
                    <CardContent className="pt-6 space-y-4">
                        <div className="grid gap-3 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <Label className="text-xs">Title *</Label>
                                <Input
                                    value={editing.title}
                                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                                    placeholder="e.g. Photo attachments are now live"
                                    data-testid="admin-news-title-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs">Category *</Label>
                                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                                    <SelectTrigger data-testid="admin-news-category-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {NEWS_CATEGORIES.map(c => (
                                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end gap-4 pb-1">
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={editing.is_published}
                                        onChange={(e) => setEditing({ ...editing, is_published: e.target.checked })}
                                        data-testid="admin-news-published-checkbox"
                                    />
                                    Publish
                                </label>
                                <label className="flex items-center gap-2 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={editing.is_pinned}
                                        onChange={(e) => setEditing({ ...editing, is_pinned: e.target.checked })}
                                        data-testid="admin-news-pinned-checkbox"
                                    />
                                    Pin to top
                                </label>
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs">Body *</Label>
                            <Textarea
                                value={editing.body}
                                onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                                rows={6}
                                placeholder="Write the post body here. Plain text — line breaks are preserved."
                                data-testid="admin-news-body-input"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setEditing(null)} data-testid="admin-news-cancel">
                                <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                            <Button onClick={save} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="admin-news-save">
                                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                                {editing.id ? 'Save changes' : (editing.is_published ? 'Publish' : 'Save draft')}
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* List */}
            {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
            ) : posts.length === 0 ? (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                        <p>No posts yet. Click <strong>New post</strong> to author your first announcement.</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-2">
                    {posts.map(p => {
                        const meta = NEWS_CATEGORIES.find(c => c.value === p.category) || NEWS_CATEGORIES[0];
                        const Icon = meta.icon;
                        return (
                            <Card key={p.id} data-testid={`admin-news-row-${p.id}`}>
                                <CardContent className="py-4">
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div className="flex-1 min-w-[200px]">
                                            <div className="flex items-center gap-2 flex-wrap mb-1">
                                                <Badge className={`${meta.bg} text-white text-[10px]`}>
                                                    <Icon className="w-3 h-3 mr-1" /> {meta.label}
                                                </Badge>
                                                {p.is_pinned && <Badge variant="outline" className="text-[10px]"><Pin className="w-2.5 h-2.5 mr-1" />Pinned</Badge>}
                                                {p.is_published ? (
                                                    <Badge className="bg-emerald-500 text-white text-[10px]">Published</Badge>
                                                ) : (
                                                    <Badge variant="outline" className="text-[10px] border-amber-400 text-amber-700 dark:text-amber-300">Draft</Badge>
                                                )}
                                                <span className="text-[10px] text-muted-foreground ml-1">
                                                    {p.published_at ? format(parseISO(p.published_at), 'MMM d, yyyy') : `created ${format(parseISO(p.created_at), 'MMM d')}`}
                                                </span>
                                            </div>
                                            <p className="font-semibold text-sm">{p.title}</p>
                                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1">{p.body}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <Button size="icon" variant="ghost" onClick={() => togglePublish(p)} title={p.is_published ? 'Unpublish' : 'Publish'} data-testid={`admin-news-publish-${p.id}`}>
                                                {p.is_published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                            </Button>
                                            <Button size="icon" variant="ghost" onClick={() => togglePin(p)} title={p.is_pinned ? 'Unpin' : 'Pin'} data-testid={`admin-news-pin-${p.id}`}>
                                                {p.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                                            </Button>
                                            <Button size="icon" variant="ghost" onClick={() => startEdit(p)} title="Edit" data-testid={`admin-news-edit-${p.id}`}>
                                                <Edit2 className="w-4 h-4" />
                                            </Button>
                                            <Button size="icon" variant="ghost" onClick={() => remove(p)} title="Delete" className="text-rose-600" data-testid={`admin-news-delete-${p.id}`}>
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
